// per specs/060-agreed-alerts/plan.md C1 (the threshold checks the catalog table enforces)
// The rules of the group form, apart from the DOM so they can be tested without one.
import type { useTranslate } from '@tolgee/react';
import type { GroupPayload, ThresholdKind, WatchedGroup } from './watched-groups-api';

type Translate = ReturnType<typeof useTranslate>['t'];

/** "20 %" or "50 unități", or the words for a group that has no threshold yet. */
export function thresholdLabel(
  group: Pick<WatchedGroup, 'thresholdKind' | 'thresholdValue'>,
  t: Translate,
): string {
  if (group.thresholdKind === null || group.thresholdValue === null) {
    return t('watched_groups.threshold.none');
  }
  return t(`watched_groups.threshold.value.${group.thresholdKind}`, {
    value: group.thresholdValue,
  });
}

export const GROUP_NAME_MAX = 120;

// numeric(14,3): up to eleven integer digits and three decimals.
const THRESHOLD_PATTERN = /^\d{1,11}([.,]\d{1,3})?$/;

export interface GroupFormState {
  name: string;
  description: string;
  active: boolean;
  /** '' is "no threshold". */
  thresholdKind: ThresholdKind | '';
  thresholdValue: string;
}

export interface GroupFormErrors {
  name?: string;
  thresholdValue?: string;
}

export function emptyGroupForm(): GroupFormState {
  return { name: '', description: '', active: true, thresholdKind: '', thresholdValue: '' };
}

export function groupToForm(group: WatchedGroup): GroupFormState {
  return {
    name: group.name,
    description: group.description,
    active: group.active,
    thresholdKind: group.thresholdKind ?? '',
    thresholdValue: group.thresholdValue === null ? '' : String(group.thresholdValue),
  };
}

export function parseThresholdValue(raw: string): number | null {
  const trimmed = raw.trim();
  if (!THRESHOLD_PATTERN.test(trimmed)) return null;
  return Number(trimmed.replace(',', '.'));
}

/** The translation key of each refusal, so the page decides the wording and the rules stay testable. */
export function validateGroupForm(form: GroupFormState): GroupFormErrors {
  const errors: GroupFormErrors = {};
  const name = form.name.trim();
  if (name.length === 0) errors.name = 'form.field.required';
  else if (name.length > GROUP_NAME_MAX) errors.name = 'watched_groups.form.name_too_long';

  if (form.thresholdKind !== '') {
    if (form.thresholdValue.trim().length === 0) {
      errors.thresholdValue = 'form.field.required';
    } else {
      const value = parseThresholdValue(form.thresholdValue);
      if (value === null || value <= 0)
        errors.thresholdValue = 'watched_groups.form.threshold_invalid';
      else if (form.thresholdKind === 'PERCENT' && value > 100) {
        errors.thresholdValue = 'watched_groups.form.percent_range';
      }
    }
  }
  return errors;
}

export function hasGroupErrors(errors: GroupFormErrors): boolean {
  return Object.keys(errors).length > 0;
}

/** A kind without a value, or a value without a kind, never leaves the form: both or neither. */
export function formToPayload(form: GroupFormState): GroupPayload {
  const value = form.thresholdKind === '' ? null : parseThresholdValue(form.thresholdValue);
  const hasThreshold = form.thresholdKind !== '' && value !== null;
  return {
    name: form.name.trim(),
    description: form.description.trim() === '' ? null : form.description.trim(),
    thresholdKind: hasThreshold && form.thresholdKind !== '' ? form.thresholdKind : null,
    thresholdValue: hasThreshold ? value : null,
    active: form.active,
  };
}

/** The identity line of a DCI + dose + form member or option. */
export function dciLine(parts: { dci: string; dose: string; form: string }): string {
  return [parts.dci, parts.dose, parts.form].filter((part) => part.length > 0).join(' · ');
}

/** An option the member list already holds is not offered again (unique (group, dci, dose, form), C1). */
export function sameTriple(
  a: { dci: string; dose: string; form: string },
  b: { dci: string; dose: string; form: string },
): boolean {
  const norm = (value: string): string => value.trim().toLowerCase();
  return (
    norm(a.dci) === norm(b.dci) && norm(a.dose) === norm(b.dose) && norm(a.form) === norm(b.form)
  );
}
